import { useState } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/db7ta1fidngsrf4ehpcg';

export default function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const fd = new FormData(form);

    const honeypot = String(fd.get('website_alt') || '').trim();
    if (honeypot) {
      // bot detected: show the same generic feedback, submit nothing
      setStatus('success');
      return;
    }
    fd.delete('website_alt');

    setStatus('submitting');
    setErrorMsg('');
    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const code = parsed?.code;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      const isOk = response.ok && code === 'OK' && !isSpam;

      if (isOk) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setErrorMsg(
          typeof serverMsg === 'string' && serverMsg && !isSpam
            ? serverMsg
            : '提交失败，请稍后重试或检查填写内容。'
        );
      }
    } catch {
      setStatus('error');
      setErrorMsg('网络异常，请稍后重试。');
    }
  };

  return (
    <form
      id="jianqing-contact-form"
      data-readdy-form
      onSubmit={onSubmit}
      className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-7"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <label htmlFor="contact-name" className="text-sm text-foreground-700">称呼</label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="请输入您的称呼"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
          />
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor="contact-email" className="text-sm text-foreground-700">邮箱</label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="用于接收回复，如 name@example.com"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
          />
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-2">
        <label htmlFor="contact-topic" className="text-sm text-foreground-700">留言主题</label>
        <select
          id="contact-topic"
          name="topic"
          defaultValue="内容建议"
          className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 focus:border-primary-500 focus:outline-none"
        >
          <option value="内容建议">内容建议</option>
          <option value="作品补充">作品补充</option>
          <option value="资讯投稿">资讯投稿</option>
          <option value="合作咨询">合作咨询</option>
          <option value="其他">其他</option>
        </select>
      </div>

      <div className="mt-4 flex flex-col gap-2">
        <label htmlFor="contact-message" className="text-sm text-foreground-700">留言内容</label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请输入您的留言内容（500 字以内）"
          className="resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-950 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none"
        ></textarea>
        <p className="text-xs text-foreground-500">内容建议请尽量具体，便于我们参考改进。</p>
      </div>

      <input
        className="form-hp-field"
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-5 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-foreground-950 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === 'submitting' ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              提交中…
            </>
          ) : (
            <>
              <i className="ri-send-plane-line"></i>
              提交留言
            </>
          )}
        </button>

        {status === 'success' && (
          <p className="flex items-center gap-2 text-sm text-accent-400">
            <i className="ri-checkbox-circle-fill"></i>
            留言已提交，感谢您的反馈，我们会尽快回复。
          </p>
        )}
        {status === 'error' && (
          <p className="flex items-center gap-2 text-sm text-primary-300">
            <i className="ri-error-warning-fill"></i>
            {errorMsg}
          </p>
        )}
      </div>
    </form>
  );
}